import { APPROVAL_GROUPS, PRODUCTION_STAGES, PHASE_LABEL, formatMoney } from '../../lib/journey.js'
import { useState, useEffect } from 'react'
import { api } from '../../lib/api.js'
import AdvanceOrder from './AdvanceOrder.jsx'
import { useAccess } from '../../lib/access.jsx'
import { ACTIONS, ACTION_META, sameAccount, normalizeResource } from '../../lib/rbac.js'
import { roomLinkFor, EXTERNAL_LABEL } from '../../lib/surfaces.js'
import styles from './AccountDetail.module.css'

/** The journey actions the permission layer knows about, in journey order. */
const JOURNEY_ACTIONS = [
  ACTIONS.JOURNEY_APPROVAL_ADVANCE,
  ACTIONS.JOURNEY_PRODUCTION_UPDATE,
  ACTIONS.JOURNEY_DELIVERY_UPDATE,
  ACTIONS.JOURNEY_ORDER_HOLD,
]

/**
 * Read-only account detail: the full 7-group approval journey + 14-stage
 * production tracker, derived from the deal's stage. Approval groups show as
 * complete/current/pending; the production tracker highlights the current stage.
 */
export default function AccountDetail({ deal, onClose, onChanged }) {
  /* The list's own ordered statuses. Fetched here rather than baked in, so the
   * picker can never offer a status the board does not define — and so adding
   * a stage in ClickUp needs no frontend change. */
  const [statuses, setStatuses] = useState([])
  const [approvalsBound, setApprovalsBound] = useState(null)

  useEffect(() => {
    let live = true
    api.journeyModel()
      .then((m) => {
        if (!live) return
        setStatuses(Array.isArray(m?.statuses) ? m.statuses : [])
        setApprovalsBound(m?.approvalGroups?.binding?.bound === true)
      })
      .catch(() => { if (live) { setStatuses([]); setApprovalsBound(null) } })
    return () => { live = false }
  }, [])

  const done = deal.approvalsDone
  const currentKey = deal.currentGroup?.key
  const inProduction = deal.phase === 'production' || deal.complete
  const curStageN = deal.productionStage?.n || 0

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className={styles.head}>
          <div>
            <div className="eyebrow">{deal.orderNo || deal.dealNo} · {PHASE_LABEL[deal.phase]}</div>
            <h3 className={styles.title}>{deal.org}</h3>
            <div className={styles.sub}>
              {deal.sport}{deal.qty ? ` · ${deal.qty} units` : ''}{deal.amount != null ? ` · ${formatMoney(deal.amount)}` : ''} · {deal.owner}
            </div>
          </div>
          <button className={styles.close} onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className={styles.provenance}>
          {deal.source === 'clickup'
            ? <>Derived from the ClickUp order status <code>{deal.dealstageRaw || '—'}</code> · status moves write back to ClickUp</>
            : <>Derived from HubSpot deal stage <code>{deal.dealstageRaw || '—'}</code> · read-only</>}
        </div>

        <AccessPanel deal={deal} />

        <RoomCrossLink deal={deal} />

        <div className={styles.section}>
          <div className="eyebrow">Approval journey · 7 groups</div>

          {/* ⚠️ THE RECORD OF THESE SEVEN IS NOT HERE, AND SAYING SO IS THE
            * POINT. Brian's rule: anything the customer signs is external, so
            * the signatures live in the Team Meeting Room, which is where the
            * customer actually clicks them and where the proof version they
            * were shown is pinned. What follows is a REFERENCE COPY of the
            * seven groups, not the record — and this screen never claims
            * otherwise. Measured 2026-09-11: with the model unbound, every one
            * of these renders "pending" on all 3,142 accounts, so a reader who
            * thought this was the record would be reading a blank as a fact. */}
          <div className={styles.notYet}>
            <strong>The signatures live in the {EXTERNAL_LABEL}, not here.</strong> This is a
            reference copy of the seven groups so an internal reader knows what the customer is
            being asked for. The customer signs in their room, against the exact proof version
            they were shown — that is the record, and it is the only one.
          </div>

          {!deal.approvalsKnown && (
            <div className={styles.notYet}>
              <strong>No approval is on record for this order.</strong> Every group reads unknown.
              The order&rsquo;s status says where it is in production, and that is not a signature,
              so nothing here is inferred from it: an order in production still shows unknown rather
              than a check nobody gave.
            </div>
          )}

          {/* RECORDING an approval is deliberately absent, and this says why.
            * Two things block it, both real:
            *   1. The approval model is UNBOUND — the live board expresses no
            *      confirmed set of groups, so there is no valid `group` to
            *      record against.
            *   2. An approval requires a manifest of exactly what the approver
            *      was shown. The store refuses a row without one, on purpose,
            *      because an approval with no evidence of what was approved is
            *      worse than no record at all.
            * A button here would 400 every time. Saying so is more useful. */}
          {approvalsBound === false && (
            <div className={styles.notYet}>
              <strong>Approvals cannot be recorded yet.</strong> The seven groups are not bound to
              the live board, so there is no group to sign against — and an approval has to carry
              proof of exactly what the customer was shown. Binding the model is an executive
              decision; until then this stays a view rather than a button that would fail.
            </div>
          )}
          <ol className={styles.approvals}>
            {APPROVAL_GROUPS.map((g) => {
              const state = !deal.approvalsKnown
                ? 'unknown'
                : done.has(g.key) ? 'done' : g.key === currentKey ? 'current' : 'pending'
              return (
                <li key={g.key} className={`${styles.appRow} ${styles[state]}`}>
                  <span className={styles.appDot}>{state === 'done' ? '✓' : g.num}</span>
                  <span className={styles.appTitle}>{g.title}</span>
                  <span className={styles.appOwner}>{g.owner}</span>
                  <span className={styles.appState}>{state}</span>
                </li>
              )
            })}
          </ol>
        </div>

        <div className={styles.section}>
          <div className="eyebrow">Production tracker · 14 stages</div>
          {inProduction ? (
            curStageN ? (
            <div className={styles.stages}>
              {PRODUCTION_STAGES.map((s) => {
                const state = s.n < curStageN ? 'done' : s.n === curStageN ? 'current' : 'pending'
                return (
                  <div key={s.key} className={`${styles.stageRow} ${styles[state]}`}>
                    <span className={styles.stageN}>{s.n}</span>
                    <span className={styles.stageTitle}>{s.title}</span>
                    {state === 'current' && <span className={styles.stageNow}>in progress</span>}
                  </div>
                )
              })}
            </div>
            ) : <div className={styles.notYet}>Individual manufacturing milestones are not recorded in this feed. The order status does not establish completion of the 14 production stages.</div>
          ) : (
            <div className={styles.notYet}>Production begins after all approvals are signed and the order is released to manufacturing.</div>
          )}
        </div>

        {/* ---------------------------------------------------------------
          * THE ORDERS THEMSELVES.
          *
          * This was the missing half of the screen. An "account" here is a
          * GROUP of orders — a school that ordered rings and pendants and a
          * add-on run is three tasks — and the modal showed a single derived
          * status for the whole group with no way to reach any of them. So
          * the one thing a rep actually wants, "open the order I am being
          * asked about", was the one thing the page could not do.
          *
          * Each row is the real ClickUp task with its real status, and the
          * link goes straight to it. Nothing here writes yet; the write
          * routes exist on the backend and are gated, and wiring them is a
          * separate, deliberate step rather than something to slip in behind
          * a bug fix.
          * --------------------------------------------------------------- */}
        {deal.orders?.length > 0 && (
          <div className={styles.section}>
            <div className="eyebrow">
              Orders · {deal.orders.length}
              {deal.owners?.length > 0 && <> · {deal.owners.join(', ')}</>}
            </div>
            <ul className={styles.orderList}>
              {deal.orders.map((o) => (
                <li key={o.id} className={styles.orderRow}>
                  <div className={styles.orderMain}>
                    <span className={styles.orderName}>{o.name || o.id}</span>
                    <span className={styles.orderStatus}>{o.status || '—'}</span>
                  </div>
                  <div className={styles.orderMeta}>
                    {o.phase && <span className={styles.orderPhase}>{o.phase}</span>}
                    {o.url && (
                      <a
                        className={styles.orderLink}
                        href={o.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open in ClickUp
                      </a>
                    )}
                  </div>
                  <div className={styles.orderAction}>
                    <AdvanceOrder
                      order={o}
                      deal={deal}
                      statuses={statuses}
                      onDone={onChanged}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * THE PER-ACCOUNT CROSS-LINK into the external surface.
 *
 * ⚠️ IT LINKS THE CONSOLE, NOT A GUESSED DEEP LINK, AND IT SAYS WHY.
 *
 * The portal does have a per-account room — `/portal?account=<id>`, read off
 * its own staff console on 2026-09-11. But that id is the PORTAL's, and this
 * record carries a ClickUp task id and a school name. Nothing maps one to the
 * other on either side today, and `/staff` reads no query parameters at all,
 * so the search cannot even be pre-filled.
 *
 * Composing the URL anyway would land a rep on nothing, or on ANOTHER TEAM'S
 * ROOM — a customer-facing record, opened against the wrong customer. The
 * account name is printed here to search for instead. That costs one search;
 * the alternative costs a great deal more, and would cost it silently.
 *
 * Closing this is a portal-side change (docs/BACKEND-HANDOVER.md): accept `?q=`
 * on /staff, or key portal accounts by `sr_school_key` so both systems can name
 * the same account. When it lands, `roomLinkFor()` in lib/surfaces.js is the
 * only thing that changes.
 */
function RoomCrossLink({ deal }) {
  const link = roomLinkFor(deal)
  return (
    <div className={styles.section}>
      <div className="eyebrow">The customer’s side of this order</div>
      <p className={styles.accessNote}>
        Approvals, signatures, the roster, the review ask, marketing material, delivery assets and
        the contract for <strong>{deal.org}</strong> are recorded in the {EXTERNAL_LABEL} — that is
        the surface the customer sees, and the only place any of it counts.
      </p>
      <a
        className={styles.orderLink}
        href={link.href}
        target="_blank"
        rel="noopener noreferrer"
      >
        Open the team console ↗
      </a>
      {!link.deepLinked && (
        <p className={styles.accessNote}>
          It opens on the console rather than this account: the portal keys rooms by its own
          account id, which this record does not carry. Search <strong>{deal.org}</strong> there.
          Guessing the id would risk opening another team’s room.
        </p>
      )}
    </div>
  )
}

/**
 * Account control + the permission readout for THIS account.
 *
 * Every line is a live `can(action, deal)` call — resource-scoped, so a rep
 * sees "allowed" on their own book and the reason on everyone else's. The
 * journey is still read-only, so this deliberately states what the actor WOULD
 * be permitted to do rather than offering a button that has no write route
 * behind it. When the journey write endpoints land, each row becomes the
 * control itself with no rule duplicated here.
 */
function AccessPanel({ deal }) {
  const { can, why, actingAs, startActingAs, stopActingAs, roleMeta } = useAccess()
  const active = actingAs && sameAccount(actingAs, normalizeResource(deal))
  const mayControl = can(ACTIONS.ACTING_START, deal)

  if (deal.journeyScope?.entity === 'orders') {
    return <div className={styles.section}><p className={styles.accessNote}>Order read scope only; no CRM account control is established. Order status actions require their separate server authorization.</p></div>
  }

  return (
    <div className={styles.section}>
      <div className={styles.accessHead}>
        <div className="eyebrow">Account control · {roleMeta?.label}</div>
        {active ? (
          <button className={styles.accessBtn} onClick={stopActingAs}>Exit control</button>
        ) : mayControl ? (
          <button className={styles.accessBtn} onClick={() => startActingAs(deal)}>
            Take control
          </button>
        ) : (
          <span className={styles.accessWhy}>{why(ACTIONS.ACTING_START, deal)}</span>
        )}
      </div>

      <ul className={styles.accessList}>
        {JOURNEY_ACTIONS.map((a) => {
          const ok = can(a, deal)
          return (
            <li key={a} className={styles.accessRow} data-ok={ok}>
              <span className={styles.accessDot}>{ok ? '✓' : '—'}</span>
              <span className={styles.accessLabel}>{ACTION_META[a].label}</span>
              <span className={styles.accessState}>{ok ? 'allowed' : why(a, deal)}</span>
            </li>
          )
        })}
      </ul>

      <p className={styles.accessNote}>
        Permissions are drawn from the shared policy in <code>lib/rbac.js</code>. They govern this
        UI only — sr-portal-backend must re-check role and account ownership on every write before
        any of this is enforcement.
      </p>
    </div>
  )
}

